import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
export type UserRole = 'Admin' | 'Manager' | 'User' | 'Viewer';
export interface Workspace {
  id: string;
  name: string;
  type: 'Staging' | 'Production' | 'Development';
}
export interface UserSession {
  id: string;
  email: string;
  fullName?: string;
  role: UserRole;
  workspaces: Workspace[];
  activeWorkspaceId: string;
}
interface AuthState {
  isAuthenticated: boolean;
  user: UserSession | null;
  isLoading: boolean;
  login: (user: UserSession) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  switchWorkspace: (workspaceId: string) => void;
  updateRole: (role: UserRole) => void;
  updateProfile: (updates: Partial<Pick<UserSession, 'fullName' | 'email'>>) => void;
}
const DEFAULT_WORKSPACES: Workspace[] = [
  { id: 'ws_prod', name: 'Global Production', type: 'Production' },
  { id: 'ws_staging', name: 'Staging Sandbox', type: 'Staging' },
];
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      user: null,
      isLoading: false,
      login: (user) => set({ isAuthenticated: true, user, isLoading: false }),
      logout: () => set({ isAuthenticated: false, user: null, isLoading: false }),
      setLoading: (loading) => set({ isLoading: loading }),
      switchWorkspace: (workspaceId) => set((state) => ({
        user: state.user ? { ...state.user, activeWorkspaceId: workspaceId } : null
      })),
      updateRole: (role) => set((state) => ({
        user: state.user ? { ...state.user, role } : null
      })),
      updateProfile: (updates) => set((state) => ({
        user: state.user ? { ...state.user, ...updates } : null
      })),
    }),
    {
      name: 'cloudface-auth-session-v2',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
// Individual primitive selectors (Strict Compliance)
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);
export const useAuthEmail = () => useAuthStore((s) => s.user?.email ?? null);
export const useAuthFullName = () => useAuthStore((s) => s.user?.fullName ?? null);
export const useAuthId = () => useAuthStore((s) => s.user?.id ?? null);
export const useAuthLoading = () => useAuthStore((s) => s.isLoading);
export const useUserRole = () => useAuthStore((s) => s.user?.role ?? 'Viewer');
export const useActiveWorkspaceId = () => useAuthStore((s) => s.user?.activeWorkspaceId ?? 'ws_prod');
export const useUserWorkspaces = () => useAuthStore((s) => s.user?.workspaces ?? []);
// Derivations
export const useActiveWorkspace = () => {
  const workspaces = useUserWorkspaces();
  const activeId = useActiveWorkspaceId();
  return workspaces.find(w => w.id === activeId) ?? DEFAULT_WORKSPACES[0];
};
// Action selectors
export const useAuthLogin = () => useAuthStore((s) => s.login);
export const useAuthLogout = () => useAuthStore((s) => s.logout);
export const useAuthSetLoading = () => useAuthStore((s) => s.setLoading);
export const useSwitchWorkspace = () => useAuthStore((s) => s.switchWorkspace);
export const useUpdateUserRole = () => useAuthStore((s) => s.updateRole);
export const useUpdateProfile = () => useAuthStore((s) => s.updateProfile);
